import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icons } from "../lib/icons";
import { Btn, CopyBtn, LivenessPill, PersonaPill, ResultPill, FactorChip, Modal } from "../lib/primitives";
import { fmt } from "../lib/format";
import { useAuditLog } from "../lib/hooks";
import type { AuditEventRow } from "../lib/api";
import { classifyLiveness, LIVENESS_REASON_LABELS } from "../lib/liveness";

/**
 * The server's audit_events table is the closest thing to "auth logs" the
 * auth-server has today (the rich design schema — factors, latency,
 * proofHash — isn't yet exposed). We filter to verification.* actions and
 * adapt to the design row shape so the rest of the table renders unchanged.
 */
/**
 * Bucket an audit action into the Event filter's categories.
 * verified / failed are verification outcomes; admin = operator / config /
 * security actions; created = persona / enrollment / factor lifecycle.
 */
function categoryOf(action: string): "verified" | "failed" | "admin" | "created" {
  if (action === "verification.success") return "verified";
  if (action === "verification.failed") return "failed";
  if (
    action.startsWith("provider.") ||
    action.startsWith("api_key.") ||
    action.startsWith("session.")
  )
    return "admin";
  return "created";
}

/** Human label for an audit action, shown in the Result column. */
function friendlyAction(action: string, meta: Record<string, unknown>): string {
  const scope = typeof meta.scope === "string" ? meta.scope : undefined;
  if (action === "provider.updated") {
    return scope
      ? `${scope.charAt(0).toUpperCase()}${scope.slice(1)} updated`
      : "Config updated";
  }
  const labels: Record<string, string> = {
    "provider.created": "Organization created",
    "api_key.rotated": "API key created",
    "api_key.revoked": "API key revoked",
    "persona.created": "Person added",
    "persona.deactivated": "Person deactivated",
    "enrollment.created": "Set up",
    "enrollment.updated": "Sign-in updated",
    "enrollment.deleted": "Sign-in removed",
    "factor.updated": "Method updated",
    "session.revoked": "Signed out",
    "team.invitation.created": "Member invited",
    "team.invitation.accepted": "Invitation accepted",
    "team.member.role_changed": "Role changed",
    "team.member.removed": "Member removed",
  };
  const known = labels[action];
  if (known) return known;
  const pretty = action.replace(/[._]/g, " ");
  return pretty.charAt(0).toUpperCase() + pretty.slice(1);
}

/** Download the given (already filtered) log rows as a CSV file. */
function downloadLogsCsv(rows: any[]): void {
  const headers = ["Timestamp", "Category", "Result", "User", "Persona", "Factors", "Liveness", "Liveness reason", "Event ID"];
  const cell = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(",")];
  for (const l of rows) {
    lines.push(
      [
        new Date(l.ts).toISOString(),
        l.category,
        l.result,
        l.userExtId,
        l.category === "admin" ? "" : l.persona,
        l.factors.join(" "),
        l.livenessState,
        l.liveness?.reason,
        l.id,
      ]
        .map(cell)
        .join(","),
    );
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `auth-logs-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function adaptAuditEvent(e: AuditEventRow, i: number) {
  const ts = new Date(e.createdAt);
  const meta = (e.metadata ?? {}) as Record<string, unknown>;
  const result =
    e.action === "verification.success"
      ? "success"
      : e.action === "verification.failed"
        ? "fail-factor"
        : friendlyAction(e.action, meta);
  const persona =
    (meta.personaType as string | undefined) === "agent" ? "agent" : "human";
  const factors = Array.isArray(meta.factors)
    ? (meta.factors as string[])
    : [];
  const { liveness, state: livenessState } = classifyLiveness(e);
  return {
    id: e.id,
    ts: e.createdAt,
    tsLabel: ts.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }),
    dateLabel: ts.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
    userExtId: (meta.externalUserId as string) ?? (meta.actor as string) ?? e.externalUserId ?? e.providerSubject ?? "–",
    persona,
    factors,
    result,
    category: categoryOf(e.action),
    hasExternalId:
      typeof meta.externalUserId === "string" || typeof e.externalUserId === "string",
    scheme: typeof meta.schemeId === "string" ? (meta.schemeId as string) : null,
    reason: typeof meta.reason === "string" ? (meta.reason as string) : null,
    liveness,
    livenessState,
    _action: e.action,
    _severity: e.severity,
    _serverBacked: true,
    _idx: i,
  };
}

const SCREEN_TO_PATH: Record<string, string> = {
  dashboard: "/",
  quickstart: "/quickstart",
  logs: "/logs",
  users: "/users",
  keys: "/keys",
  policy: "/policy",
  branding: "/sign-in-page",
  hosted: "/sign-in-page",
  team: "/team",
  billing: "/billing",
  settings: "/settings",
};

function Detail({ label, value, mono }: { label: React.ReactNode; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="ad-field">
      <div className="ad-field-label">{label}</div>
      <div style={{ fontSize: mono ? 12 : 13, fontFamily: mono ? "var(--rb-font-mono)" : "inherit", color: "var(--ad-fg-1)" }}>{value}</div>
    </div>
  );
}

export function Logs() {
  const navigate = useNavigate();
  const onNav = (id: string) => navigate(SCREEN_TO_PATH[id] ?? "/");

  const [persona, setPersona] = useState("all"); // all | human | agent
  const [category, setCategory] = useState("all"); // all | admin | created | verified | failed
  const [liveness, setLiveness] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<any>(null);

  const { data: auditEvents, isLoading } = useAuditLog({ limit: 100 });
  const rows = (auditEvents ?? []).map(adaptAuditEvent);

  const filtered = useMemo(() => {
    return rows.filter((l: any) => {
      if (persona !== "all" && l.persona !== persona) return false;
      if (category !== "all" && l.category !== category) return false;
      if (liveness !== "all" && (liveness === "failed" ? !l.livenessState.startsWith("failed") : l.livenessState !== liveness)) return false;
      if (query && !(l.userExtId.includes(query) || l.id.includes(query))) return false;
      return true;
    });
  }, [persona, category, liveness, query, rows]);

  return (
    <>
      <div className="ad-page-head">
        <div className="ad-page-head-l">
          <div className="ad-eyebrow">Monitor</div>
          <h1 className="ad-h1">Activity</h1>
          <div className="ad-sub">
            Every sign-in and change for your app. You see what happened and when, never what
            someone used to sign in.
          </div>
        </div>
        <div className="ad-page-head-r">
          <Btn kind="ghost" leading={<Icons.Download size={14} />} disabled={filtered.length === 0} onClick={() => downloadLogsCsv(filtered)}>Export CSV</Btn>
        </div>
      </div>

      {/* Filter bar */}
      <div className="ad-filterbar">
        <div className="ad-top-search" style={{ flex: "0 1 320px", border: "1px solid var(--ad-border-2)" }}>
          <Icons.Search size={14} />
          <input placeholder="Search by external user ID or event ID" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div style={{ width: 1, height: 22, background: "var(--ad-hairline)" }} />
        <span className="ad-eyebrow-sm">Who</span>
        <button className="ad-filter-chip" data-active={persona === "all"} onClick={() => setPersona("all")}>All</button>
        <button className="ad-filter-chip" data-active={persona === "human"} onClick={() => setPersona("human")}>People</button>
        <button className="ad-filter-chip" data-active={persona === "agent"} onClick={() => setPersona("agent")}>AI agents</button>
        <div style={{ width: 1, height: 22, background: "var(--ad-hairline)" }} />
        <span className="ad-eyebrow-sm">Event</span>
        <button className="ad-filter-chip" data-active={category === "all"} onClick={() => setCategory("all")}>All</button>
        <button className="ad-filter-chip" data-active={category === "admin"} onClick={() => setCategory("admin")}>Changes</button>
        <button className="ad-filter-chip" data-active={category === "created"} onClick={() => setCategory("created")}>Set up</button>
        <button className="ad-filter-chip" data-active={category === "verified"} onClick={() => setCategory("verified")}>Signed in</button>
        <button className="ad-filter-chip" data-active={category === "failed"} onClick={() => setCategory("failed")}>Failed</button>
        <div style={{ width: 1, height: 22, background: "var(--ad-hairline)" }} />
        <span className="ad-eyebrow-sm">Liveness</span>
        {[["all", "All"], ["passed", "Passed"], ["failed", "Failed"], ["not-run", "Not run"]].map(([id, label]) => <button key={id} className="ad-filter-chip" data-active={liveness === id} onClick={() => setLiveness(id)}>{label}</button>)}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: "var(--ad-fg-3)" }}>{fmt(filtered.length)} events</span>
      </div>

      {isLoading ? (
        <div className="ad-card-flat" style={{ padding: 32, textAlign: "center", color: "var(--ad-fg-3)", fontSize: 13 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="ad-card-flat">
          <div className="ad-empty" style={{ border: 0, margin: 0 }}>
            <div className="ad-empty-title">Nothing yet</div>
            <div className="ad-empty-sub">Sign-ins show up here as soon as people start using AuthenSee in your app.</div>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="ad-card-flat">
          <div className="ad-empty" style={{ border: 0, margin: 0 }}>
            <div className="ad-empty-title">No matching events</div>
            <div className="ad-empty-sub">Try another search or filter.</div>
          </div>
        </div>
      ) : (
        <div className="ad-card-flat" style={{ padding: 0, overflowX: "auto" }}>
        <table className="ad-table">
          <thead>
            <tr>
              <th style={{ width: 110 }}>Time</th>
              <th>Event</th>
              <th>External user ID</th>
              <th>Who</th>
              <th>Method</th>
              <th>Result</th>
              <th>Liveness</th>
              <th style={{ width: 24 }}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 80).map((l: any) => (
              <tr key={l.id} className="is-selectable" onClick={() => setSelected(l)}>
                <td>
                  <div className="mono" style={{ fontWeight: 500, color: "var(--ad-fg-1)" }}>{l.tsLabel}</div>
                  <div style={{ fontSize: 12, color: "var(--ad-fg-3)" }}>{l.dateLabel}</div>
                </td>
                <td>
                  <div className="ad-mono" style={{ fontSize: 12, color: "var(--ad-fg-2)" }}>{l.id}</div>
                </td>
                <td className="mono">{l.userExtId}</td>
                <td>{l.category === "admin" ? <span style={{ color: "var(--ad-fg-4)" }}>–</span> : <PersonaPill persona={l.persona} />}</td>
                <td><div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{l.factors.map((f: string) => <FactorChip key={f} name={f} />)}</div></td>
                <td><ResultPill result={l.result} /></td>
                <td><LivenessPill state={l.livenessState} /></td>
                <td><Icons.ChevronR size={14} className="row-arrow" /></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {/* Event detail drawer */}
      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        eyebrow="Event"
        title={selected?.id || ""}
        width={720}
        footer={
          <>
            <Btn kind="ghost" onClick={() => setSelected(null)}>Close</Btn>
            {selected && selected.category !== "admin" && (
              <Btn kind="primary" leading={<Icons.Person size={14} />} onClick={() => { onNav("users"); setSelected(null); }}>See people</Btn>
            )}
          </>
        }
      >
        {selected && (() => {
          const isVerif = selected.category === "verified" || selected.category === "failed";
          const isPersona = selected.category !== "admin";
          return (
            <div className="ad-stack-lg">
              <div className="ad-grid-2">
                <Detail label={isPersona ? (selected.hasExternalId ? "User external ID" : "Provider subject") : "Changed by"} value={selected.userExtId} mono />
                {isPersona && <Detail label="Who" value={<PersonaPill persona={selected.persona} />} />}
                <Detail label="Time" value={new Date(selected.ts).toUTCString()} mono />
                <Detail label="Result" value={<ResultPill result={selected.result} />} />
                {selected.scheme && <Detail label="Scheme" value={selected.scheme} mono />}
                {selected.reason && <Detail label="Failure reason" value={selected.reason} />}
                {isVerif && selected.factors.length > 0 && (
                  <Detail label="Methods checked" value={<div style={{ display: "flex", gap: 4 }}>{selected.factors.map((f: string) => <FactorChip key={f} name={f} />)}</div>} />
                )}
              </div>
              {selected.livenessState !== "not-run" && <div>
                <div className="ad-eyebrow-sm" style={{ marginBottom: 8 }}>Liveness</div>
                <div className="ad-grid-2">
                  <Detail label="Outcome" value={<LivenessPill state={selected.livenessState} />} />
                  <Detail label="Mode" value={`${selected.liveness.mode} / effective ${selected.liveness.effectiveMode}${selected.liveness.clamped ? " (clamped)" : ""}`} />
                  {selected.liveness.reason && <Detail label="Reason" value={LIVENESS_REASON_LABELS[selected.liveness.reason] ?? selected.liveness.reason} />}
                  {selected.liveness.teePubkeyHash && <Detail label="TEE public-key hash" value={selected.liveness.teePubkeyHash} mono />}
                </div>
              </div>}
              <div>
                <div className="ad-eyebrow-sm" style={{ marginBottom: 8 }}>Event ID</div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 12, background: "var(--ad-surface-2)", borderRadius: 12, fontFamily: "var(--rb-font-mono)", fontSize: 13 }}>
                  <span>{selected.id}</span>
                  <CopyBtn text={selected.id} small />
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>
    </>
  );
}
